import { formatDate, Pagination, Text } from '@amelio/ui-library';

import type { ManagerLaunchStatus } from '@/types/cycle-evaluation/ManagerLaunchStatus';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';

type ManagerStatusSectionProps = {
    isLaunched: boolean;
    isLoading: boolean;
    managers: ManagerLaunchStatus[];
    totalCount: number;
    onPageChange: (page: number) => void;
    currentPage: number;
    totalPages: number;
};

const PAGE_SIZE = 5;

const ManagerStatusSection = ({
    isLaunched,
    isLoading,
    managers,
    totalCount,
    onPageChange,
    currentPage,
    totalPages,
}: ManagerStatusSectionProps) => {
    const { t } = useAmelioTranslations();

    if (isLoading) {
        return (
            <Skeleton className={'bg-base-50 flex flex-col gap-2'}>
                {Array.from({ length: 5 }, (_, index) => (
                    <Bone
                        key={index}
                        className={'bg-base h-16 w-full rounded-lg'}
                    />
                ))}
            </Skeleton>
        );
    }

    if (managers.length === 0) {
        return (
            <div
                className={'py-6 text-center'}
                data-testid={`${isLaunched ? 'launched' : 'not-launched'}-empty-state`}
            >
                <Text className={'text-center'}>
                    {t('cycleEvaluationDashboard.noResultsFound')}
                </Text>
            </div>
        );
    }

    return (
        <>
            <div className={'scrollbar-glass min-h-0 flex-1 overflow-y-auto'}>
                {managers.map((manager) => (
                    <div
                        key={manager.id}
                        className={`${isLaunched ? 'grid grid-cols-[1fr_1fr_1fr]' : 'grid grid-cols-[2fr_1fr]'} border-base-100 items-center gap-4 border-b px-4 py-4`}
                        data-testid={`${isLaunched ? 'launched' : 'not-launched'}-row-${manager.id}`}
                    >
                        <Text size={'small'}>{manager.managerName}</Text>

                        <div className={'flex items-center justify-center gap-2'}>
                            <div className={'bg-base-200 h-2 flex-1 rounded-full'}>
                                <div
                                    className={'h-2 rounded-full bg-purple-600'}
                                    style={{
                                        width: `${(manager.launchedReviews / manager.requiredReviews) * 100}%`,
                                    }}
                                />
                            </div>

                            <Text
                                size={'small'}
                                className={'text-sm'}
                            >
                                {`${manager.launchedReviews} / ${manager.requiredReviews}`}
                            </Text>
                        </div>

                        {isLaunched && manager.launchedOn && (
                            <Text
                                size={'small'}
                                className={'text-right'}
                            >
                                {formatDate({
                                    date: manager.launchedOn,
                                    language: i18n.language,
                                })}
                            </Text>
                        )}
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={PAGE_SIZE}
                    onPageChange={onPageChange}
                    isLoading={isLoading}
                    dataTestId={`${isLaunched ? 'launched' : 'not-launched'}-pagination`}
                    resultsSummaryText={t('common.showingResults', {
                        start: totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                        end: Math.min(currentPage * PAGE_SIZE, totalCount),
                        total: totalCount,
                    })}
                />
            )}
        </>
    );
};
export default ManagerStatusSection;
